import React from 'react';
import {
    Box, Typography, Paper, Grid, Chip, Divider,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Tune as TuneIcon,
    Block as BlockIcon,
    Edit as EditIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import { getRegionDisplayName } from '../../types';

interface CheckpointSummaryCardProps {
    modifications?: {
        weights?: Record<string, number>;
        excluded_regions?: string[];
        context_overrides?: Record<string, Record<string, any>>;
        batch_overrides?: Record<string, Record<string, any>>;
        national_build_target?: number;
        policy_toggles?: Record<string, boolean>;
    };
}

const CheckpointSummaryCard: React.FC<CheckpointSummaryCardProps> = ({ modifications }) => {
    if (!modifications) return null;

    const hasWeights = modifications.weights && Object.keys(modifications.weights).length > 0;
    const hasExclusions = modifications.excluded_regions && modifications.excluded_regions.length > 0;
    const hasOverrides = modifications.context_overrides && Object.keys(modifications.context_overrides).length > 0;
    const hasBatchOverrides = modifications.batch_overrides && Object.keys(modifications.batch_overrides).length > 0;
    const hasNationalTarget = typeof modifications.national_build_target === 'number';
    const hasPolicyToggles = modifications.policy_toggles && Object.keys(modifications.policy_toggles).length > 0;

    if (!hasWeights && !hasExclusions && !hasOverrides && !hasBatchOverrides && !hasNationalTarget && !hasPolicyToggles) return null;

    return (
        <Accordion
            elevation={0}
            sx={{
                mb: 2,
                border: '1px solid #e0e0e0',
                borderRadius: '8px !important',
                '&:before': { display: 'none' }
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <HistoryIcon color="primary" />
                    <Typography sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                        User Modification Log
                    </Typography>
                    <Chip
                        label="Human-in-the-Loop Overrides Applied"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 600, ml: 1 }}
                    />
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                    {hasWeights && (
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <TuneIcon fontSize="small" color="action" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Weighting Factors</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {Object.entries(modifications.weights!).map(([k, v]) => (
                                    <Chip
                                        key={k}
                                        label={`${k}: ${(v * 100).toFixed(0)}%`}
                                        size="small"
                                        variant="filled"
                                        sx={{ bgcolor: '#f1f8ff', color: '#0366d6', fontWeight: 500 }}
                                    />
                                ))}
                            </Box>
                        </Grid>
                    )}

                    {hasExclusions && (
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <BlockIcon fontSize="small" color="error" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Excluded Regions</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {modifications.excluded_regions!.map(region => (
                                    <Chip
                                        key={region}
                                        label={getRegionDisplayName(region)}
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Grid>
                    )}

                    {hasOverrides && (
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <EditIcon fontSize="small" color="warning" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Data Overrides</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {Object.keys(modifications.context_overrides!).length} region(s) adjusted manually.
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                {Object.entries(modifications.context_overrides!).map(([region, fields]) => (
                                    <Typography key={region} variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                                        • {getRegionDisplayName(region)}: {Object.keys(fields).join(', ')}
                                    </Typography>
                                ))}
                            </Box>
                        </Grid>
                    )}

                    {hasBatchOverrides && (
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <EditIcon fontSize="small" color="primary" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Batch Quantity Overrides</Typography>
                            </Box>
                            <Box sx={{ mt: 1 }}>
                                {Object.entries(modifications.batch_overrides!).map(([scope, values]) => (
                                    <Typography key={scope} variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                                        • {scope}: {Object.entries(values).map(([k, v]) => `${k}=${v}`).join(', ')}
                                    </Typography>
                                ))}
                            </Box>
                        </Grid>
                    )}

                    {(hasNationalTarget || hasPolicyToggles) && (
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <TuneIcon fontSize="small" color="action" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Program-Level Overrides</Typography>
                            </Box>
                            {hasNationalTarget && (
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                    National Build Target: {modifications.national_build_target!.toLocaleString()}
                                </Typography>
                            )}
                            {hasPolicyToggles && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                    Policies: {Object.entries(modifications.policy_toggles!)
                                        .filter(([, enabled]) => !!enabled)
                                        .map(([k]) => k)
                                        .join(', ') || 'None enabled'}
                                </Typography>
                            )}
                        </Grid>
                    )}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};

export default CheckpointSummaryCard;
